export class ProductRegistryError extends Error {
    code;
    detail;
    constructor(code, detail) {
        super(detail === undefined ? code : `${code}: ${detail}`);
        this.name = "ProductRegistryError";
        this.code = code;
        this.detail = detail;
    }
}
const ROUTE_BASE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62})$/;
const API_NAMESPACE_PATTERN = /^\/api\/v1\/[a-z0-9](?:[a-z0-9-]{0,62})$/;
const SEMVER_PATTERN = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const TOKEN_PATTERN = /^[a-z0-9](?:[a-z0-9._-]{0,127})$/;
function nonEmpty(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function assertManifest(manifest) {
    if (!nonEmpty(manifest.key) ||
        !nonEmpty(manifest.displayName) ||
        !ROUTE_BASE_PATTERN.test(manifest.routeBase) ||
        !API_NAMESPACE_PATTERN.test(manifest.apiNamespace) ||
        !nonEmpty(manifest.requiredEntitlement) ||
        !["active", "preview", "disabled"].includes(manifest.status) ||
        !SEMVER_PATTERN.test(manifest.minimumPlatformVersion) ||
        !Array.isArray(manifest.navigationItems) ||
        !Array.isArray(manifest.publicResourceTypes)) {
        throw new ProductRegistryError("invalid_manifest", String(manifest.key));
    }
    const navigationKeys = new Set();
    for (const item of manifest.navigationItems) {
        if (!TOKEN_PATTERN.test(item.key) ||
            !nonEmpty(item.label) ||
            (item.section !== "" && !TOKEN_PATTERN.test(item.section)) ||
            (item.order !== undefined && !Number.isSafeInteger(item.order)) ||
            (item.requiredEntitlement !== undefined && !nonEmpty(item.requiredEntitlement)) ||
            navigationKeys.has(item.key)) {
            throw new ProductRegistryError("invalid_manifest", `${manifest.key}.navigationItems`);
        }
        navigationKeys.add(item.key);
    }
    const publicTypes = new Set();
    for (const resourceType of manifest.publicResourceTypes) {
        if (!TOKEN_PATTERN.test(resourceType) || publicTypes.has(resourceType)) {
            throw new ProductRegistryError("invalid_manifest", `${manifest.key}.publicResourceTypes`);
        }
        publicTypes.add(resourceType);
    }
}
function freezeManifest(manifest) {
    return Object.freeze({
        ...manifest,
        navigationItems: Object.freeze(manifest.navigationItems.map((item) => Object.freeze({ ...item }))),
        publicResourceTypes: Object.freeze([...manifest.publicResourceTypes]),
    });
}
export function createProductRegistry(manifests) {
    const byKey = new Map();
    const byRoute = new Map();
    const byApi = new Map();
    for (const candidate of manifests) {
        assertManifest(candidate);
        if (byKey.has(candidate.key)) {
            throw new ProductRegistryError("duplicate_product_key", candidate.key);
        }
        if (byRoute.has(candidate.routeBase)) {
            throw new ProductRegistryError("duplicate_route_base", candidate.routeBase);
        }
        if (byApi.has(candidate.apiNamespace)) {
            throw new ProductRegistryError("duplicate_api_namespace", candidate.apiNamespace);
        }
        const manifest = freezeManifest(candidate);
        byKey.set(manifest.key, manifest);
        byRoute.set(manifest.routeBase, manifest);
        byApi.set(manifest.apiNamespace, manifest);
    }
    const ordered = Object.freeze([...byKey.values()].sort((left, right) => left.key.localeCompare(right.key)));
    return Object.freeze({
        manifests: ordered,
        get: (productKey) => byKey.get(productKey),
        getByRouteBase: (routeBase) => byRoute.get(routeBase),
        getByApiNamespace: (apiNamespace) => byApi.get(apiNamespace),
        has: (productKey) => byKey.has(productKey),
    });
}
export const REPUTATION_PRODUCT_MANIFEST = Object.freeze({
    key: "reputation",
    displayName: "Review Boost",
    routeBase: "reputation",
    apiNamespace: "/api/v1/reputation",
    navigationItems: Object.freeze([]),
    requiredEntitlement: "reputation.access",
    status: "active",
    minimumPlatformVersion: "0.1.0",
    publicResourceTypes: Object.freeze(["reviews"]),
});
export const MENU_PRODUCT_MANIFEST = Object.freeze({
    key: "menu",
    displayName: "Menu Digital",
    routeBase: "menu",
    apiNamespace: "/api/v1/menu",
    navigationItems: Object.freeze([]),
    requiredEntitlement: "menu.access",
    status: "active",
    minimumPlatformVersion: "0.1.0",
    publicResourceTypes: Object.freeze(["menu"]),
});
export const CUSTOM_PRODUCT_MANIFEST = Object.freeze({
    key: "custom",
    displayName: "Aplicaciones personalizadas",
    routeBase: "custom",
    apiNamespace: "/api/v1/custom",
    navigationItems: Object.freeze([]),
    requiredEntitlement: "custom.access",
    status: "active",
    minimumPlatformVersion: "0.1.0",
    publicResourceTypes: Object.freeze(["custom-app"]),
});
export const SALONS_PRODUCT_MANIFEST = Object.freeze({
    key: "salons",
    displayName: "Peluquerías",
    routeBase: "salons",
    apiNamespace: "/api/v1/salons",
    navigationItems: Object.freeze([
        Object.freeze({ key: "agenda", label: "Agenda", section: "agenda", order: 10 }),
        Object.freeze({ key: "conversations", label: "Conversaciones", section: "conversations", order: 20 }),
        Object.freeze({ key: "customers", label: "Clientes", section: "customers", order: 30 }),
        Object.freeze({ key: "services", label: "Servicios", section: "services", order: 40 }),
    ]),
    requiredEntitlement: "salons.access",
    status: "active",
    minimumPlatformVersion: "0.1.0",
    publicResourceTypes: Object.freeze([]),
});
export const CORE_PRODUCT_REGISTRY = createProductRegistry([
    CUSTOM_PRODUCT_MANIFEST,
    REPUTATION_PRODUCT_MANIFEST,
    MENU_PRODUCT_MANIFEST,
    SALONS_PRODUCT_MANIFEST,
]);
//# sourceMappingURL=product-manifest.js.map